import React, { useContext, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Typography,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  Button,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { SwapVert, Refresh } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { Delete } from "@mui/icons-material";
import { CMSContext } from "../context/CaseManagmentContext";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { showInsufficientCreditsModal } from "../../../actions/creditActions";
import { deductCredits } from "../../../utils/HelperFunction/deductCredits";
import ConfirmationDialog from "../../../dialogs/ConfirmationDialog";
import AddIcon from "@mui/icons-material/Add";
import AddNewLawyerModal from "../../../dialogs/AddNewLawyerDialog";
import { toast } from "react-toastify";
import CenteredPagination from "../../UI/CenteredPagination";
import { deleteLawyerApi } from "../../../services/api/caseManagementApi";
// const baseUrl = "http://localhost:7071/api";

const LawyersPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { advocates, fetchAdvocates } = useContext(CMSContext);
  const dispatch = useDispatch();
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const [selectedItem, setSelectedItem] = useState({});
  const [openEditModal, setOpenEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const rowsPerPage = 10;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchAdvocates();
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch advocates list from API
  useEffect(() => {
    fetchAdvocates();
  }, []);

  // Handle sorting logic
  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const sortedData =
    advocates &&
    [...advocates?.filter((item) => item.name !== null)]?.sort((a, b) => {
      const isAsc = sortConfig.direction === "asc";
      // Default sorting for other keys
      if (sortConfig.key === "name") {
        return isAsc
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      return 0; // If no valid sort key, return 0 (no sorting)
    });

  // Handle search functionality
  const filteredData = sortedData?.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle pagination
  const paginatedData = filteredData?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  const handleDeleteLawyer = async (lawyerId) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }
    setLoading(true);
    try {
      const response = await deleteLawyerApi(lawyerId);

      if (response.status === 200) {
        fetchAdvocates();
        const deductionSuccess = deductCredits(
          {
            userId,
            creditsToDeduct: 1,
            event: "Delete Lawyer on Case Management",
          },
          dispatch
        );

        if (!deductionSuccess) {
          console.warn(
            "Failed to deduct credits. Ensure retry or user notification."
          );
        }
        toast.success("Lawyer deleted successfully");
      } else {
        const data = response.data;
        toast.error(data.message);
        console.error("Error deleting lawyer:", data);
      }
    } catch (error) {
      console.error("Error deleting lawyer:", error);
    } finally {
      setLoading(false);
      setDeleteModal(false);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          boxSizing: "border-box",
          flexGrow: 1,
          bgcolor: "#fff",
          borderRadius: "8px",
          p: { xs: "8px", lg: "16px 16px 8px 16px" },
        }}
      >
        <Stack
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" }, // Column on mobile, row on larger screens
            justifyContent: "space-between",
            gap: { xs: 2, sm: 0 }, // Add gap between elements on mobile
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", sm: "400px" }, // Full width on mobile
              mt: { xs: 0, sm: 1 }, // Adjust top margin for mobile
              mb: { xs: 0, sm: 2 },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ccc",
                },
                "&:hover fieldset": {
                  borderColor: "#00509e",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#00509e",
                },
              },
              "& input": {
                padding: "10px 10px",
                fontSize: "0.875rem",
              },
            }}
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => {
              setPage(1);
              setSearchTerm(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "#303f9f", }} />
              ),
              sx: {
                pl: 1,
                backgroundColor: "#ffffff",
                borderRadius: "20px",
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: { xs: "100%", sm: "auto" },
              mt: { xs: 0, sm: "16px" },
              mb: { xs: "16px", sm: "16px" },
            }}
          >
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
                sx={{
                  border: "1px solid #003D78",
                  borderRadius: "6px",
                  color: "#003D78",
                  p: "6px",
                  "&:hover": { backgroundColor: "rgba(0,61,120,0.08)" },
                }}
              >
                {refreshing ? (
                  <CircularProgress size={18} sx={{ color: "#003D78" }} />
                ) : (
                  <Refresh fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: "#003D78",
                display: "flex",
                justifyContent: "center",
                width: { xs: "100%", sm: "auto" },
                py: "6px",
                textTransform: "none",
                "&:hover": { backgroundColor: "primary.main", color: "#fff" },
                transition: "background-color 0.3s ease-in-out",
              }}
              onClick={() => {
                setSelectedItem({});
                setOpenEditModal(true);
              }}
            >
              Add Lawyer
            </Button>
          </Box>
        </Stack>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "79%",
            boxShadow: "none",
            borderRadius: "12px",
            overflowY: "auto",
            "::-webkit-scrollbar": {
              width: "5px",
            },
            "::-webkit-scrollbar-track": {
              background: "#fff",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#bbb",
              borderRadius: "6px",
            },
            "::-webkit-scrollbar:horizontal": {
              height: "4px",
            },
            "::-webkit-scrollbar-thumb:horizontal": {
              background: "#bbb",
              borderRadius: "6px",
            },
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <TableCell
                  sx={{ fontWeight: 600, color: "#101936", py: "6px" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Name
                    <IconButton
                      onClick={() => handleSort("name")}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <SwapVert fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#101936",
                    py: "6px",
                  }}
                >
                  Phone
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#101936",
                    py: "6px",
                  }}
                >
                  Email
                </TableCell>

                {/* <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#101936",
                    py: "6px",
                  }}
                >
                  CNIC
                </TableCell> */}
                {/* <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#101936",
                    py: "6px",
                  }}
                >
                  Designation
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#101936",
                    py: "6px",
                  }}
                >
                  Address
                </TableCell> */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#101936",
                    py: "6px",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData?.length > 0 ? (
                paginatedData?.map((item, index) => (
                  <TableRow
                    key={`${item.chat_id}-${index}`}
                    sx={{
                      backgroundColor: "white",
                      transition: "all 0.3s",
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.05)",
                      },
                      "&:not(:last-child)": {
                        borderBottom: "1px solid #f0f0f0",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        py: 2,
                        color: "#101936",
                        fontSize: "13px",
                        fontWeight: 500,
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                          color: "#3C50B1",
                        },
                      }}
                      onClick={() => {
                        setSelectedItem(item);
                      }}
                    >
                      {truncateText(item.name, 100)}
                    </TableCell>

                    <TableCell
                      sx={{
                        py: 2,
                        maxWidth: "150px",
                        fontSize: "13px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {item.phone || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        maxWidth: "150px",
                        fontSize: "13px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {item.email}
                    </TableCell>
                    {/* <TableCell
                      sx={{
                        py: 2,
                        maxWidth: "150px",
                        fontSize: "13px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {item.cnic}
                    </TableCell> */}
                    {/* <TableCell
                      sx={{
                        py: 2,
                        maxWidth: "150px",
                        fontSize: "13px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {item.designation || "N/A"}
                    </TableCell> */}
                    {/* <TableCell
                      sx={{
                        py: 2,
                        maxWidth: "150px",
                        fontSize: "13px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#101936",
                      }}
                    >
                      {item.address || "N/A"}
                    </TableCell> */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          sx={{
                            color: "inherit",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                          onClick={(event) => {
                            setSelectedItem(item);
                            setAnchorEl(event.currentTarget);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={() => {
                            setSelectedItem({});
                            setAnchorEl(null);
                          }}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          PaperProps={{
                            sx: {
                              //mt: 3.5,
                              boxShadow:
                                "0px 4px 12px rgba(0, 0, 0, 0.1) !important",
                              borderRadius: "8px",
                            },
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              setDeleteModal(true);
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon size="xs">
                              <Delete
                                fontSize="small"
                                sx={{ color: "#d2042d", fontSize: "16px" }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Delete
                            </Typography>
                          </MenuItem>
                        </Menu>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography sx={{ fontSize: "14px" }}>
                      No records found matching your search
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            fontSize: { xs: "12px", md: "14px" },
            mt: 1,
            width: "100%",
            overflowY: "auto",
            "& .MuiTablePagination-root": {
              width: "100%",

              maxWidth: { xs: "100%", sm: "80%", md: "60%" },
            },
            "& .MuiTablePagination-toolbar": {
              p: 0,
              flexWrap: "wrap",
              justifyContent: "center",
            },
            "& .MuiSelect-select": {
              fontSize: { xs: "12px", md: "14px" },
            },
            "& .MuiInputBase-root": {
              fontSize: { xs: "12px", md: "14px" },
            },
            "& .MuiTablePagination-actions": {
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
            },
          }}
        >
          <CenteredPagination
            totalResults={filteredData?.length}
            resultsCount={rowsPerPage}
            currentPage={page}
            onPageChange={handleChangePage}
          />
        </Box>
      </Box>
      <AddNewLawyerModal
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          fetchAdvocates();     
        }}
        data={selectedItem}
      />
      <ConfirmationDialog
        open={deleteModal}
        onClose={() => {
          setDeleteModal(false);
        }}
        onConfirm={() => {
          if (deleteModal) {
            handleDeleteLawyer(selectedItem?._id);
          }
        }}
        title="Confirm Delete"
        contentText={`Are you sure you want to delete the lawyer "${selectedItem?.name}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        loading={loading}
      />
    </Box>
  );
};

export default LawyersPage;
